import React, { useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Dialog } from "primereact/dialog";
import accountService from "../../services/account/account.service";
import { useQuery } from "@tanstack/react-query";
import { FilterMatchMode } from "primereact/api";
import { Column } from "primereact/column";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { commonFilterContains, commonFilterDateIs, CreateHeader, Filter } from "../filter/base-filter";
import { EmailUserDto, RequestSendMassMailDto } from "../../services/account/account.dto";
import { Editor, EditorTextChangeEvent } from "primereact/editor";
import { useToast } from "../../utils/toast";
import AdminService from "../../services/admin/admin.services";

const MassEmail = () => {
  const toast = useToast();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const dt = useRef<DataTable<EmailUserDto[]>>(null);
  const [selectedUserListesi, setSelectedUserListesi] = useState<EmailUserDto[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const {
    data: UsersMailResponse,
    refetch: refetchUsersMail,
    isLoading: usersResponseLoading
  } = useQuery({
    queryKey: ["UsersMail"],
    refetchOnMount: false,
    queryFn: async () => await AdminService.getUserForMassEmail()
  });

  const clearFilter = () => {
    initFilters();
  };

  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      islemYapanBirim: commonFilterContains,
      safahatTuruAciklama: commonFilterContains,
      safahatTarihiDate: commonFilterDateIs,
      aciklama: commonFilterContains,
      personel: commonFilterContains
    });
    setGlobalFilterValue("");
  };

  useEffect(() => {
    initFilters();
  }, []);

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let _filters = { ...filters };
    // @ts-ignore
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUserListesi.length === 0) {
      setError("Lütfen en az bir kullanıcı seçiniz");
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmSend = async () => {
    setLoading(true);
    setSuccess("");
    setError("");
    setShowConfirmDialog(false);

    try {
      const request: RequestSendMassMailDto = {
        subject: subject,
        message: message,
        selected_users: selectedUserListesi.map((user) => user.id)
      };
      const response = await AdminService.sendMassEmail(request);

      if (response?.result) {
        setSuccess(`${response?.result?.data}`);
        setSubject("");
        setMessage("");
        toast.show(`${response?.result?.data}`, "success");
      } else {
        setError(`${response.data.message}`);
      }
    } catch (error) {
      toast.show("Bir hata oluştu, lütfen tekrar deneyiniz.", "error");
    }
  };

  const confirmDialogFooter = (
    <div>
      <Button label="İptal" icon="pi pi-times" onClick={() => setShowConfirmDialog(false)} className="p-button-text" />
      <Button label="Gönder" icon="pi pi-check" onClick={handleConfirmSend} autoFocus />
    </div>
  );

  const handleSendWithGoogle = () => {
    if (!selectedUserListesi || selectedUserListesi.length === 0) {
      alert("Lütfen en az bir kullanıcı seçiniz");
      return;
    }

    // Nesne içindeki email adreslerini al
    const emailList = selectedUserListesi.map((user) => user.email).filter(Boolean);

    if (emailList.length === 0) {
      alert("Seçili kullanıcıların e-posta adresleri bulunamadı.");
      return;
    }

    // E-posta adreslerini virgülle birleştir ve encode et
    const emailString = encodeURIComponent(emailList.join(","));

    // Gmail URL'si oluştur
    const gmailURL = `https://mail.google.com/mail/?view=cm&fs=1&to=${emailString}`;

    // Gmail uygulamasını aç
    window.open(gmailURL, "_blank");
  };

  return (
    <div className="flex flex-column justify-content-center">
      <div className="card col-12">
        <form onSubmit={handleSubmit} className="flex flex-column gap-3">
          {success && <Message severity="success" text={success} className="w-full" />}
          {error && <Message severity="error" text={error} className="w-full" />}

          <div className="flex flex-column gap-2">
            <label htmlFor="subject" className="font-bold">
              E-posta Konusu
            </label>
            <InputText
              id="subject"
              value={subject}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)}
              required
              className="w-full"
            />
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="message" className="font-bold">
              E-posta İçeriği
            </label>

            <Editor
              value={message}
              onTextChange={(e: EditorTextChangeEvent) => setMessage(e.htmlValue ?? "")}
              style={{ height: "320px" }}
            />
          </div>

          <Button
            type="submit"
            label={loading ? "Gönderiliyor..." : "Önizle ve E-posta Gönder"}
            icon="pi pi-send"
            loading={loading}
            disabled={loading || !subject || !message}
            className="w-full mt-3"
          />
        </form>
      </div>

      <Button
        onClick={handleSendWithGoogle}
        type="submit"
        label="Mail için liste oluştur Gönder"
        icon="pi pi-send"
        className="w-full my-3"
      />
      <div className="card col-12">
        <DataTable
          value={(UsersMailResponse?.result?.data || []) as EmailUserDto[]}
          ref={dt}
          dataKey="id"
          showGridlines
          rows={10}
          rowsPerPageOptions={[10, 25, 50, 100]}
          emptyMessage="Kullanıcı bulunamadı."
          loading={usersResponseLoading}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          filters={filters}
          globalFilterFields={["email", "first_name", "last_name", "osb", "osb_gorevi"]}
          header={CreateHeader("Kullanıcılar", globalFilterValue, (e) => onGlobalFilterChange(e), clearFilter)}
          columnResizeMode="expand"
          selectionMode="multiple"
          selection={selectedUserListesi}
          onSelectionChange={(e) => setSelectedUserListesi(e.value)}
        >
          <Column selectionMode="multiple" headerStyle={{ width: "3rem" }}></Column>
          <Column
            field="email"
            header="E-posta"
            sortable
            filterField="email"
            filter
            filterPlaceholder="E-posta ile ara"
            filterClear={Filter.FilterClearTemplate}
            filterApply={Filter.FilterApplyTemplate}
            filterFooter={() => {
              return <div className="px-3 pt-0 pb-3 text-center">E-posta ile filtrele</div>;
            }}
          />
          <Column
            field="full_name"
            header="Ad Soyad"
            sortable
            filterField="full_name"
            filter
            filterPlaceholder="Ad Soyad ile ara"
            filterClear={Filter.FilterClearTemplate}
            filterApply={Filter.FilterApplyTemplate}
            filterFooter={() => {
              return <div className="px-3 pt-0 pb-3 text-center">Ad Soyad ile filtrele</div>;
            }}
          />
          <Column
            field="osb"
            header="Kurum"
            sortable
            filterField="osb"
            filter
            filterPlaceholder="Kurum ile ara"
            filterClear={Filter.FilterClearTemplate}
            filterApply={Filter.FilterApplyTemplate}
            filterFooter={() => {
              return <div className="px-3 pt-0 pb-3 text-center">Kurum ile filtrele</div>;
            }}
          />
          <Column
            field="osb_gorevi"
            header="Görev"
            sortable
            filterField="osb_gorevi"
            filter
            filterPlaceholder="Görev ile ara"
            filterClear={Filter.FilterClearTemplate}
            filterApply={Filter.FilterApplyTemplate}
            filterFooter={() => {
              return <div className="px-3 pt-0 pb-3 text-center">Görev ile filtrele</div>;
            }}
          />
        </DataTable>
      </div>

      <Dialog
        visible={showConfirmDialog}
        style={{ width: "50vw" }}
        header="E-posta Onayı"
        modal
        footer={confirmDialogFooter}
        onHide={() => setShowConfirmDialog(false)}
      >
        <div className="flex flex-column gap-4">
          <div>
            <h3>E-posta Önizleme</h3>
            <div className="p-2 border-1 surface-border border-round">
              <p>
                <strong>Konu:</strong> {subject}
              </p>
              <p>
                <strong>İçerik:</strong>
              </p>
              <div dangerouslySetInnerHTML={{ __html: message }} className="p-2 border-1 surface-border border-round" />
            </div>
          </div>

          <div>
            <h3>Seçili Alıcılar ({selectedUserListesi.length})</h3>
            <div className="p-2 border-1 surface-border border-round" style={{ maxHeight: "200px", overflow: "auto" }}>
              {selectedUserListesi.map((user, index) => (
                <div key={user.id} className="mb-2">
                  {user.email} ({user.full_name})
                </div>
              ))}
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default MassEmail;
